/**
 * RadialBarChartCard Component
 * Circular progress-style chart perfect for showing percentages/completion rates
 *
 * @description Shared radial bar chart for analytics dashboards (admin and provider)
 */

import { TrendingUp } from "lucide-react";
import { PolarAngleAxis, PolarGrid, RadialBar, RadialBarChart } from "recharts";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import type { ChartConfig } from "../ui/chart";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";
import { cn } from "../ui/cn";

interface RadialBarChartDataItem {
  readonly name: string;
  readonly value: number;
  readonly fill: string;
}

interface RadialBarChartCardProps {
  readonly data: RadialBarChartDataItem[];
  readonly title: string;
  readonly description?: string;
  readonly chartConfig: ChartConfig;
  readonly footerLabel?: string;
  readonly valueLabel?: string;
  /** Optional className for card container (e.g., h-full for grid layouts) */
  readonly className?: string;
}

export function RadialBarChartCard({
  data,
  title,
  description,
  chartConfig,
  footerLabel = "items",
  valueLabel = "Value",
  className,
}: RadialBarChartCardProps) {
  if (data.length === 0) {
    return (
      <Card className={cn("border-neutral-800 bg-neutral-900/50 flex flex-col", className)}>
        <CardHeader>
          <CardTitle className="text-white">{title}</CardTitle>
          {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[300px] text-neutral-500">No data available</div>
        </CardContent>
      </Card>
    );
  }

  // Take top 5 items for radial chart (too many gets cluttered)
  const chartData = data.slice(0, 5);
  const avgValue = Math.round(chartData.reduce((sum, item) => sum + item.value, 0) / chartData.length);

  return (
    <Card
      className={cn(
        "border-neutral-800 bg-neutral-900/50 hover:bg-neutral-900/70 transition-colors flex flex-col",
        className,
      )}
    >
      <CardHeader>
        <CardTitle className="text-white">{title}</CardTitle>
        {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="px-0">
        <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[300px]">
          <RadialBarChart data={chartData} startAngle={90} endAngle={-270} innerRadius={30} outerRadius={140}>
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel nameKey="name" />} />
            <PolarGrid
              gridType="circle"
              radialLines={false}
              stroke="none"
              className="first:fill-neutral-900 last:fill-neutral-800/50"
              polarRadius={[36, 56, 76, 96, 116]}
            />
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar
              dataKey="value"
              background
              cornerRadius={10}
              label={{
                position: "insideStart",
                fill: "#fff",
                fontSize: "12px",
                formatter: (value: unknown) => `${String(value)}%`,
              }}
            />
          </RadialBarChart>
        </ChartContainer>

        {/* Legend with title names */}
        <div className="mt-4 px-4 space-y-2">
          {chartData.map((item) => (
            <div key={item.name} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }} />
                <span className="text-neutral-300 truncate max-w-[200px]">{item.name}</span>
              </div>
              <span className="text-white font-medium">{item.value}%</span>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm pt-4">
        <div className="flex items-center gap-2 font-medium leading-none text-white">
          Average {avgValue}% retention <TrendingUp className="h-4 w-4 text-green-500" />
        </div>
        <div className="leading-none text-neutral-500">
          Top {chartData.length} {footerLabel} by {valueLabel.toLowerCase()}
        </div>
      </CardFooter>
    </Card>
  );
}
